/**
 * Helpers for AUSTIN incident URLs and Case IDs.
 *
 * `prod` is replaced at build time by KatalWebpackPlugin with the deployed
 * stage (beta/gamma/prod), same as elsewhere in the app.
 */

/** Base origin of the AUSTIN app for the current stage. */
export const getAustinBaseUrl = (): string => {
  const stage = 'prod'.toLowerCase();
  if (stage === 'gamma') return 'https://na-gamma.austin.a2z.com';
  if (stage === 'prod') return 'https://na.ehs-amazon.com';
  return 'https://na-beta.austin.a2z.com';
};

/** URL of the AUSTIN "create incident" form. */
export const getCreateIncidentUrl = (): string => `${getAustinBaseUrl()}/incidents/incident/create`;

/**
 * Append an explicit session handoff hint to an AUSTIN URL. The autofill userscript
 * reads `#navSession=<id>` on load and trusts it as the operator's chosen session for
 * that form context (bypassing the stale-session guard, which otherwise ignores any
 * session on a page that has no Case ID — e.g. the Create Incident / Initial Report
 * pages). Kept in the hash so AUSTIN's router ignores it.
 */
export const withSessionHint = (url: string, sessionId: string): string =>
  sessionId ? `${url}#navSession=${encodeURIComponent(sessionId)}` : url;

/** URL of the AUSTIN "initial report" (FROI) login/start page. */
export const getInitialReportUrl = (): string =>
  `${getAustinBaseUrl()}/incidents/initial-report/login`;

/** Build the canonical AUSTIN incident URL for a Case ID. */
export const buildIncidentUrl = (caseId: string): string =>
  `${getAustinBaseUrl()}/incidents/incident/${caseId}`;

/**
 * Normalize a Case ID: uppercase and keep only letters, digits, and hyphens.
 * AUSTIN Case IDs are uppercase (e.g. ABE2-26-28), so this canonicalizes user
 * input for storage and for case-insensitive comparison against a pasted URL.
 */
export const normalizeCaseId = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9-]/g, '');

/** AUSTIN actions that share the `/incident/{id}` path segment but aren't incidents. */
const INCIDENT_ACTIONS = ['create', 'list', 'new'];
const INCIDENT_PATH_PATTERN = /\/incident\/([^/]+)/;
const GROUP_PATH_PATTERN = /\/incidents\/group\/([^/]+)/;
const GROUP_CASE_ID_PATTERN = /-GROUP-/i;
/** A site code (three letters and a number), a two-digit year, and a sequence number. */
const INCIDENT_CASE_ID_PATTERN = /^[A-Za-z]{3}\d+-\d{2}-\d+$/;

/**
 * Whether a Case ID is an AUSTIN group number (e.g. `ABE2-26-GROUP-19`) rather than
 * an incident. AUSTIN shows the group number as the incident page heading, so users
 * often copy it by mistake.
 */
export const isGroupCaseId = (caseId: string): boolean => GROUP_CASE_ID_PATTERN.test(caseId.trim());

/**
 * Validate an AUSTIN incident Case ID, e.g. `ABE2-26-28`.
 *
 * Group numbers are rejected. The autofill userscript finds a session by the incident
 * ID on each AUSTIN form page, so a session linked to a group can never be auto-filled.
 */
export const isValidCaseId = (caseId: string): boolean => INCIDENT_CASE_ID_PATTERN.test(caseId.trim());

/** Why a Case ID can't be linked to a session, or null when it can. */
export const caseIdToLinkError = (caseId: string): string | null => {
  if (isGroupCaseId(caseId)) {
    return `"${caseId}" is an AUSTIN group number. Open the incident in AUSTIN and enter its Case ID instead, e.g. ABE2-26-28.`;
  }
  if (!isValidCaseId(caseId)) {
    return `"${caseId}" doesn't look like a valid Case ID. It should be a site code, year, and number, e.g. ABE2-26-28.`;
  }
  return null;
};

/** Validate that a string is any well-formed URL. */
export const isValidUrl = (value: string): boolean => {
  try {
    // eslint-disable-next-line no-new
    new URL(value.trim());
    return true;
  } catch {
    return false;
  }
};

/** The `/incident/{id}` segment of an AUSTIN path, unless it's an action like create. */
const pathToIncidentSegment = (path: string): string | null => {
  const id = path.match(INCIDENT_PATH_PATTERN)?.[1];
  return id && !INCIDENT_ACTIONS.includes(id) ? id : null;
};

/**
 * The incident Case ID in an AUSTIN path, e.g. ABE2-26-28 from
 * `/incidents/group/ABE2-26-GROUP-19/incident/ABE2-26-28/record`, or null. A group number
 * never counts, since every AUSTIN form page is keyed by the incident.
 */
export const pathToIncidentId = (path: string): string | null => {
  const id = pathToIncidentSegment(path);
  return id && !isGroupCaseId(id) ? id : null;
};

/** The group number in an AUSTIN path, from group pages and their incidents' pages, or null. */
export const pathToGroupId = (path: string): string | null => path.match(GROUP_PATH_PATTERN)?.[1] ?? null;

/**
 * Extract a Case ID from a full AUSTIN case URL: the `/incident/{id}` segment, or failing
 * that the group number. Returns null if none. A group link yields the group number, so
 * validation can tell the user they pasted a group link instead of silently finding nothing.
 */
export const parseCaseIdFromUrl = (url: string): string | null => {
  let pathname: string;
  try {
    pathname = new URL(url.trim()).pathname;
  } catch {
    return null;
  }
  return pathToIncidentSegment(pathname) ?? pathToGroupId(pathname);
};
